import { useState } from "react";
import Container from "@components/ui/container.tsx";
import { JobAlertsPanel, type JobAlertsPanelProps } from "@components/job_alerts/panel.tsx";
import { JobSearchFilters } from "@components/job_search/job_search_filters.tsx";
import { JobsTabs } from "@components/job_search/jobs_tabs.tsx";
import { SearchBar } from "@components/job_search/search_bar.tsx";
import { type Location } from "@components/ui/location_input.tsx";
import { type RecentSearch } from "@/types/job_search/recent_searches";
import { useEffectAfterMount } from "@hooks/use_effect_after_mount.ts";
import { buildJobsParams, recentSearchToLocation, useJobSearch } from "@hooks/use_job_search";

export type AlertsTabProps = JobAlertsPanelProps & {
  recentSearches: RecentSearch[];
};

export const AlertsTab = ({ recentSearches, ...panelProps }: AlertsTabProps) => {
  const [query, setQuery] = useState("");
  const [location, setLocation] = useState<Location | null>(null);
  const [loading, setLoading] = useState(false);
  const [datePosted, setDatePosted] = useState("");
  const [salary, setSalary] = useState("");
  const [workType, setWorkType] = useState<string[]>([]);
  const [employmentType, setEmploymentType] = useState<string[]>([]);
  const submitJobSearch = useJobSearch();

  const handleSearch = () => {
    setLoading(true);

    submitJobSearch(
      buildJobsParams(query, location, {
        date_posted: datePosted,
        salary,
        work_type: workType,
        employment_type: employmentType,
      }),
      { onFinish: () => setLoading(false) },
    );
  };

  const handleRecentSearchSelect = (search: RecentSearch) => {
    setLoading(true);

    submitJobSearch(
      buildJobsParams(search.query || undefined, recentSearchToLocation(search), search, "popover"),
      { onFinish: () => setLoading(false) },
    );
  };

  useEffectAfterMount(() => {
    handleSearch();
  }, [datePosted, salary, workType, employmentType]);

  return (
    <>
      <div className="flex flex-col gap-3 py-6 pl-7 pr-6 mb-3 bg-green-100">
        <SearchBar
          onSearch={handleSearch}
          query={query}
          onQueryChange={setQuery}
          location={location}
          onLocationChange={setLocation}
          disabled={loading}
          recentSearches={recentSearches}
          onRecentSearchSelect={handleRecentSearchSelect}
        />
        <JobSearchFilters
          fullBleed
          datePosted={datePosted}
          onDatePostedChange={setDatePosted}
          salary={salary}
          onSalaryChange={setSalary}
          workType={workType}
          onWorkTypeChange={setWorkType}
          employmentType={employmentType}
          onEmploymentTypeChange={setEmploymentType}
        />
      </div>
      <JobsTabs active="alerts" />
      <Container className="!max-w-[1550px] !pt-0">
        <JobAlertsPanel {...panelProps} variant="tab" />
      </Container>
    </>
  );
};
